import { useBookingStore } from "@stores/useBookingStore";
import PlacementChoices from "./PlacementChoices";
import { useNavigate, useLocation } from "react-router";
import { t } from "i18next";
import { Button, Icon } from "@thetreep/ui";
import { routes } from "@routes";
import { useDAP } from "../dap.hook";
import {
  getPlacementChoices,
  searchJourney,
  toInwardRequest,
} from "@services/travel";
import { useEffect, useState } from "react";
import { useLoadingEffect } from "@hooks/useLoadingEffect";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { SearchLoader } from "../search/SearchLoader";
import { localDateTimeToDate } from "@utils/date";

export function Placement() {
  const {
    outwardResults,
    inwardResults,
    outwardOffer,
    inwardOffer,
    setInwardResults,
    outwardPlacementChoices,
    inwardPlacementChoices,
    setOutwardPlacementChoices,
    setInwardPlacementChoices,
  } = useBookingStore();

  const location = useLocation();
  const { addToast } = useToaster();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useLoadingEffect(isLoading);

  const { dap } = useDAP();

  const optionsInWard = inwardPlacementChoices || undefined;
  const optionOutWard = outwardPlacementChoices || undefined;

  useEffect(() => {
    async function fetchPlacementChoices() {
      if (
        outwardOffer?.offer.id &&
        outwardResults?.travelId &&
        dap?.number &&
        !outwardPlacementChoices
      ) {
        const result = await getPlacementChoices(
          outwardResults.travelId,
          dap.number,
          [outwardOffer.offer.id],
        );
        setOutwardPlacementChoices(result);
      }

      if (
        inwardOffer?.offer.id &&
        inwardResults?.travelId &&
        dap?.number &&
        !inwardPlacementChoices
      ) {
        const result = await getPlacementChoices(
          inwardResults.travelId,
          dap.number,
          [inwardOffer.offer.id],
        );
        setInwardPlacementChoices(result);
      }
    }
    fetchPlacementChoices();
  }, [
    dap?.number,
    inwardOffer?.offer.id,
    outwardOffer?.offer.id,
    inwardResults?.travelId,
    outwardResults?.travelId,
    outwardPlacementChoices,
    inwardPlacementChoices,
    setOutwardPlacementChoices,
    setInwardPlacementChoices,
  ]);

  const navigate = useNavigate();
  const { search } = useLocation();

  const departureDate = outwardOffer?.solution?.journey?.schedule?.departure;
  const arrivalDate = inwardOffer?.solution?.journey?.schedule?.departure;

  const nextStep = async () => {
    const searchRequest = location.state?.searchRequest;
    if (outwardOffer && !inwardOffer) {
      if (
        outwardOffer.offer.ancillaries?.length &&
        outwardOffer.offer.ancillaries.length > 0
      ) {
        navigate(routes.OPTIONS + search, {
          state: { searchRequest: searchRequest, type: "outward" },
        });
      } else {
        if (!dap) return;
        setIsLoading(true);
        try {
          const response = await searchJourney(
            toInwardRequest(
              searchRequest,
              dap,
              outwardOffer.offer.id,
              outwardResults?.travelId,
            ),
          );
          setInwardResults(response);
          navigate(routes.RESULTS + search, {
            state: { searchRequest, searchResponse: response },
          });
        } catch (e) {
          if (e instanceof ConnectError) {
            addToast("error", e.rawMessage);
          }
        } finally {
          setIsLoading(false);
        }
      }
    } else if (outwardOffer && inwardOffer) {
      if (
        inwardOffer.offer.ancillaries?.length &&
        inwardOffer.offer.ancillaries.length > 0
      ) {
        navigate(routes.OPTIONS + search, {
          state: { searchRequest: searchRequest, type: "inward" },
        });
      } else {
        navigate(routes.RECAP + search);
      }
    }
  };

  return (
    <>
      {isLoading && <SearchLoader type={"return"} />}
      {outwardOffer && !inwardOffer && (
        <>
          <h1
            data-testid="journey-header"
            className="text-[24px] md:text-[32px] font-jakarta font-bold"
          >
            {t("search.outward")}:{" "}
            {outwardOffer?.solution?.journey?.route?.origin?.name} -{" "}
            {outwardOffer?.solution?.journey?.route?.destination?.name}
          </h1>
          <span className="font-jakarta text-gray-900">
            {departureDate
              ? Intl.DateTimeFormat("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }).format(localDateTimeToDate(departureDate))
              : ""}
          </span>
          <h2 className="text-2xl font-bold mb-12 mt-6">
            {t("placement.title")}
          </h2>
          <PlacementChoices
            solution={outwardOffer?.solution}
            options={optionOutWard}
          />
        </>
      )}

      {inwardOffer && (
        <>
          <h1
            data-testid="journey-header"
            className="text-[24px] md:text-[32px] font-jakarta font-bold mt-6"
          >
            {t("search.return")}:{" "}
            {inwardOffer?.solution?.journey?.route?.origin?.name} -{" "}
            {inwardOffer?.solution?.journey?.route?.destination?.name}
          </h1>
          <span className="font-jakarta text-gray-900">
            {arrivalDate
              ? Intl.DateTimeFormat("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }).format(localDateTimeToDate(arrivalDate))
              : ""}
          </span>
          <h2 className="text-2xl font-bold mb-4 mt-6">
            {t("placement.title")}
          </h2>
          <PlacementChoices
            solution={inwardOffer?.solution}
            options={optionsInWard}
          />
        </>
      )}
      <div className="mt-6 flex [&_button]:w-[100%] md:[&_button]:w-[auto] justify-end">
        <Button
          onClick={nextStep}
          disabled={!optionOutWard && !optionsInWard}
          endEnhancer={<Icon color="white" src="arrow_right_line" />}
        >
          {t("placement.next")}
        </Button>
      </div>
    </>
  );
}
